import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import constants from '../helpers/constants';
import getApiHeaders from '../helpers/apiHeaders';
import { getSpinnerFrame } from '../helpers/spinner';
import '../style/FlashcardModal.css';

export default function FlashcardModal({ topic, subtopic, fileId, neighbors, cachedCards, cachedRevealed, onCardsGenerated, onCompleted, onReveal }) {
    const { t } = useTranslation();
    const [cardStates, setCardStates] = useState(() =>
        (cachedCards || []).map(c => ({ question: c.question, answer: c.answer, flipped: false }))
    );
    const [currentIndex, setCurrentIndex] = useState(0);
    const currentIndexRef = useRef(currentIndex);
    currentIndexRef.current = currentIndex;
    const [loading, setLoading] = useState(!cachedCards);
    const [error, setError] = useState(null);
    const [sliding, setSliding] = useState('');
    const [spinnerFrame, setSpinnerFrame] = useState(getSpinnerFrame);
    const slidingRef = useRef(false);
    const [revealedCards, setRevealedCards] = useState(() => new Set(cachedRevealed || []));
    const completedRef = useRef(false);
    const onRevealRef = useRef(onReveal);
    onRevealRef.current = onReveal;
    const onCompletedRef = useRef(onCompleted);
    onCompletedRef.current = onCompleted;
    const onCardsGeneratedRef = useRef(onCardsGenerated);
    onCardsGeneratedRef.current = onCardsGenerated;
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (cachedCards || fetchedRef.current) return;
        fetchedRef.current = true;
        let cancelled = false;
        let reader = null;

        const extractCompleteCards = (text) => {
            const cards = [];
            let depth = 0, inString = false, escape = false, objStart = -1;
            for (let i = 0; i < text.length; i++) {
                const ch = text[i];
                if (escape) { escape = false; continue; }
                if (ch === '\\') { escape = true; continue; }
                if (ch === '"') { inString = !inString; continue; }
                if (inString) continue;
                if (ch === '{') { if (depth === 0) objStart = i; depth++; }
                if (ch === '}') {
                    depth--;
                    if (depth === 0 && objStart >= 0) {
                        try {
                            const obj = JSON.parse(text.slice(objStart, i + 1));
                            if (obj.question && obj.answer) cards.push(obj);
                        } catch {}
                        objStart = -1;
                    }
                }
            }
            return cards;
        };

        const fetchCards = async () => {
            try {
                const res = await fetch(constants.fetchNodeFlashcards, {
                    method: 'POST',
                    headers: getApiHeaders(),
                    credentials: 'include',
                    body: JSON.stringify({ topic, subtopic, fileId, neighbors }),
                });
                if (cancelled) return;
                if (!res.ok) throw new Error('Failed to generate flashcards');

                reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let accumulated = '';
                let extractedCount = 0;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (cancelled) break;
                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        try {
                            const event = JSON.parse(line.slice(6));
                            if (event.type === 'chunk') {
                                accumulated += event.content;
                                const allCards = extractCompleteCards(accumulated);
                                if (allCards.length > extractedCount) {
                                    const newCards = allCards.slice(extractedCount);
                                    extractedCount = allCards.length;
                                    setCardStates(prev => [
                                        ...prev,
                                        ...newCards.map(c => ({ question: c.question, answer: c.answer, flipped: false }))
                                    ]);
                                    if (extractedCount === 1) setLoading(false);
                                }
                            } else if (event.type === 'done') {
                                const finalCards = event.cards || [];
                                setCardStates(prev =>
                                    finalCards.map((c, i) => ({
                                        question: c.question,
                                        answer: c.answer,
                                        flipped: prev[i]?.flipped || false,
                                    }))
                                );
                                if (onCardsGeneratedRef.current && finalCards.length > 0) {
                                    onCardsGeneratedRef.current(finalCards);
                                }
                                setLoading(false);
                            } else if (event.type === 'error') {
                                setError(event.error);
                                setLoading(false);
                            }
                        } catch {}
                    }
                }
                if (!cancelled) setLoading(false);
            } catch (e) {
                if (cancelled) return;
                console.error('[Flashcards]', e);
                setError(e.message);
                setLoading(false);
            }
        };
        fetchCards();
        return () => {
            cancelled = true;
            fetchedRef.current = false;
            if (reader) reader.cancel();
        };
    }, [topic, subtopic, fileId, neighbors, cachedCards]);

    const currentFlipped = cardStates[currentIndex]?.flipped || false;

    useEffect(() => {
        if (!currentFlipped || cardStates.length === 0) return;
        setRevealedCards(prev => {
            if (prev.has(currentIndex)) return prev;
            const next = new Set(prev);
            next.add(currentIndex);
            if (onRevealRef.current) onRevealRef.current([...next]);
            if (!completedRef.current && next.size >= cardStates.length) {
                completedRef.current = true;
                if (onCompletedRef.current) onCompletedRef.current();
            }
            return next;
        });
    }, [currentFlipped, currentIndex, cardStates.length]);

    useEffect(() => {
        if (!loading) return;
        const id = setInterval(() => setSpinnerFrame(getSpinnerFrame()), 100);
        return () => clearInterval(id);
    }, [loading]);

    const slide = useCallback((direction, maxIndex) => {
        if (slidingRef.current) return;
        slidingRef.current = true;
        setCardStates(prev => prev.map(c => c.flipped ? { ...c, flipped: false } : c));
        setSliding(direction + '-out');
        setTimeout(() => {
            setCurrentIndex(i => {
                const next = direction === 'left' ? i + 1 : i - 1;
                if (next > maxIndex) return 0;
                if (next < 0) return maxIndex;
                return next;
            });
            setSliding(direction + '-in');
            setTimeout(() => {
                setSliding('');
                slidingRef.current = false;
            }, 180);
        }, 150);

    }, []);

    const goNext = useCallback(() => {
        slide('left', cardStates.length - 1);
    }, [cardStates.length, slide]);

    const goPrev = useCallback(() => {
        slide('right', cardStates.length - 1);
    }, [cardStates.length, slide]);

    // Swipe gesture support
    const touchStartRef = useRef(null);
    const handleTouchStart = useCallback((e) => {
        touchStartRef.current = e.touches[0].clientX;
    }, []);
    const handleTouchMove = useCallback((e) => {
        if (touchStartRef.current === null) return;
        const diff = Math.abs(touchStartRef.current - e.touches[0].clientX);
        if (diff > 10) e.preventDefault();
    }, []);
    const handleTouchEnd = useCallback((e) => {
        if (touchStartRef.current === null) return;
        const diff = touchStartRef.current - e.changedTouches[0].clientX;
        touchStartRef.current = null;
        if (Math.abs(diff) < 40) return;
        e.stopPropagation();
        if (diff > 0) goNext();
        else goPrev();
    }, [goNext, goPrev]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setCardStates(prev => prev.map((c, i) => i === currentIndexRef.current ? { ...c, flipped: !c.flipped } : c));
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [goNext, goPrev]);

    const card = cardStates[currentIndex];

    return (
        <Modal title="">
            <div className="flashcard-modal">
                <div className="flashcard-header">
                    <span className="flashcard-subtitle">{subtopic}</span>
                </div>

                {loading ? (
                    <div className="flashcard-loading">
                        <span>{spinnerFrame} {t('flashcards.generating', 'Generating flashcards...')}</span>
                    </div>
                ) : error ? (
                    <div className="flashcard-error">{error}</div>
                ) : !card ? (
                    <div className="flashcard-empty">{t('flashcards.noCards', 'No flashcards generated.')}</div>
                ) : (
                    <>
                        <div className="flashcard-wrapper">
                            <button
                                className="flashcard-nav-btn nav-left"
                                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                                aria-label="Previous"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>

                            <div
                                className={`flashcard ${currentFlipped ? 'flipped' : ''} ${sliding ? `slide-${sliding}` : ''}`}
                                onClick={() => setCardStates(prev => prev.map((c, i) => i === currentIndex ? { ...c, flipped: !c.flipped } : c))}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                <div className="flashcard-inner">
                                    <div className="flashcard-front">
                                        <div className="flashcard-content"><p>{card.question}</p></div>
                                    </div>
                                    <div className="flashcard-back">
                                        <div className="flashcard-content"><p>{card.answer}</p></div>
                                    </div>
                                </div>
                            </div>

                            <button
                                className="flashcard-nav-btn nav-right"
                                onClick={(e) => { e.stopPropagation(); goNext(); }}
                                aria-label="Next"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                            </button>
                        </div>

                        <div className="flashcard-dots">
                            {cardStates.map((_, i) => (
                                <span key={i} className={`flashcard-dot ${i === currentIndex ? 'active' : ''} ${revealedCards.has(i) ? 'revealed' : ''}`} />
                            ))}
                        </div>

                        <span className="flashcard-hint">
                            {t('flashcards.clickToFlip', 'Click card or press Space to flip')}
                        </span>
                    </>
                )}
            </div>
        </Modal>
    );
}
